import React from 'react';
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, MessageCircle } from 'lucide-react';
import { homestayInfo } from '../data/mockData';
import { Card, CardContent } from '../components/ui/card';

const Contact = () => {
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hello! I'd like to inquire about White Orchid Homestay.");
    window.open(`https://wa.me/${homestayInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  const handleCallClick = () => {
    window.location.href = `tel:${homestayInfo.phone}`;
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${homestayInfo.email}`;
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-[#1F4D3A] to-[#2d6b52] text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            We're here to help plan your perfect stay in Kurseong
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-[#F7F5F0]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            <Card className="hover:shadow-xl transition-shadow cursor-pointer" onClick={handleWhatsAppClick}>
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center mx-auto">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#1F4D3A]">WhatsApp</h3>
                <p className="text-gray-600">Best way to reach us instantly</p>
                <p className="text-lg font-bold text-[#1F4D3A]">{homestayInfo.whatsapp}</p>
                <button className="text-[#25D366] hover:underline font-medium">
                  Chat Now →
                </button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow cursor-pointer" onClick={handleCallClick}>
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 bg-[#1F4D3A] rounded-full flex items-center justify-center mx-auto">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#1F4D3A]">Phone</h3>
                <p className="text-gray-600">Call us for immediate assistance</p>
                <p className="text-lg font-bold text-[#1F4D3A]">{homestayInfo.phone}</p>
                <button className="text-[#1F4D3A] hover:underline font-medium">
                  Call Now →
                </button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow cursor-pointer" onClick={handleEmailClick}>
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 bg-[#D4A373] rounded-full flex items-center justify-center mx-auto">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#1F4D3A]">Email</h3>
                <p className="text-gray-600">Send us your queries</p>
                <p className="text-sm font-bold text-[#1F4D3A] break-all">{homestayInfo.email}</p>
                <button className="text-[#D4A373] hover:underline font-medium">
                  Send Email →
                </button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Location & Map */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Location Details */}
              <div className="space-y-8">
                <div>
                  <h2 className="text-4xl font-bold text-[#1F4D3A] mb-6">Visit Us</h2>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#1F4D3A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-[#1F4D3A]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1F4D3A] mb-2">Address</h3>
                        <p className="text-gray-700 leading-relaxed">{homestayInfo.address}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#1F4D3A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-[#1F4D3A]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1F4D3A] mb-2">Check-in/Check-out</h3>
                        <p className="text-gray-700">Check-in: 12:00 PM</p>
                        <p className="text-gray-700">Check-out: 11:00 AM</p>
                        <p className="text-sm text-gray-600 mt-2">(Flexible timing available on request)</p>
                      </div>
                    </div>

                    <div className="bg-[#F7F5F0] p-6 rounded-lg space-y-3">
                      <h3 className="font-semibold text-[#1F4D3A]">How to Reach</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start gap-2">
                          <span className="text-[#1F4D3A] font-bold">•</span>
                          <span><strong>From Siliguri:</strong> 50 km (1.5 hours drive)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#1F4D3A] font-bold">•</span>
                          <span><strong>From Darjeeling:</strong> 30 km (1 hour drive)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#1F4D3A] font-bold">•</span>
                          <span><strong>Nearest Railway:</strong> New Jalpaiguri (NJP) - 45 km</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#1F4D3A] font-bold">•</span>
                          <span><strong>Nearest Airport:</strong> Bagdogra (IXB) - 55 km</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                <div>
                  <h3 className="font-semibold text-[#1F4D3A] mb-4">Follow Us</h3>
                  <div className="flex gap-4">
                    <a
                      href={`https://www.instagram.com/${homestayInfo.social.instagram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg flex items-center justify-center transition-all hover:scale-110"
                      aria-label="Instagram"
                    >
                      <Instagram className="w-6 h-6 text-white" />
                    </a>
                    <a
                      href={`https://www.facebook.com/${homestayInfo.social.facebook}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center justify-center transition-all hover:scale-110"
                      aria-label="Facebook"
                    >
                      <Facebook className="w-6 h-6 text-white" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Google Map */}
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-[#1F4D3A]">Find Us on Map</h3>
                <div className="w-full h-96 rounded-lg overflow-hidden shadow-lg">
                  <iframe
                    src={homestayInfo.googleMapsEmbed}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="White Orchid Homestay Location"
                  ></iframe>
                </div>
                <p className="text-sm text-gray-600">
                  <strong>Location Advantage:</strong> Directly on main road - no walking or climbing required!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-[#F7F5F0]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#1F4D3A] text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-[#1F4D3A] mb-2">How do I make a booking?</h3>
                  <p className="text-gray-700">
                    Simply contact us on WhatsApp at {homestayInfo.whatsapp} or call us directly. We'll check availability and confirm your booking immediately.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-[#1F4D3A] mb-2">What's included in the food package?</h3>
                  <p className="text-gray-700">
                    Our food + lodging package (₹1,300/person/night) includes breakfast, lunch, evening snacks with tea, and dinner - all freshly prepared home-cooked meals.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-[#1F4D3A] mb-2">Are the cycling and hiking tours really complimentary?</h3>
                  <p className="text-gray-700">
                    Yes! All guests staying with us can enjoy complimentary guided cycling tours and nature hikes through Dowhill pine forest at no extra cost.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-[#1F4D3A] mb-2">Is parking available?</h3>
                  <p className="text-gray-700">
                    Yes, we have a secure enclosed parking space that can accommodate up to 6 cars within our property premises.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-[#1F4D3A] mb-2">What about the detached bathrooms?</h3>
                  <p className="text-gray-700">
                    Our detached bathrooms are just outside the respective rooms and are exclusively dedicated for your use. They feature western commode and hot water geyser, maintaining the same hygiene standards as attached bathrooms.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1F4D3A] to-[#2d6b52] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Book Your Stay?</h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Contact us now and let us help you plan your perfect Kurseong getaway
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleWhatsAppClick}
              className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all inline-flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp Us
            </button>
            <button
              onClick={handleCallClick}
              className="bg-white hover:bg-gray-100 text-[#1F4D3A] px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all inline-flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Call Now
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
